import {ActionTracking} from "../common-public/ActionTracking";
import {Action} from "@otto-ec/tracking-bct";
import {AddToBasketPerformanceMarketing} from "./AddToBasketPerformanceMarketing";
import {SheetV1} from "@otto-ec/otto-components/dist-lib/components/sheet/v1";

export class AddToBasketConfirmation {

    static sheetV1?: SheetV1;
    static confirmationUrl: string;
    static warrantyUrl: string;

    private _addingWarranty: boolean;

    constructor(private readonly _actionTracking: ActionTracking,
                private readonly _performanceMarketing: AddToBasketPerformanceMarketing) {
        this._addingWarranty = false;
    }

    initialize(): void {
        this._subscribeToSheetEvents();
    }

    private _subscribeToSheetEvents(): void {
        AddToBasketConfirmation.sheetV1?.contentLoadedEvent.on((sheetEvent: { instance: HTMLElement }) => {
            if (sheetEvent.instance.id === 'or_addToBasketConfirmation') {
                this._initializeConfirmationSheet();
            } else if (sheetEvent.instance.id === 'or_addToBasketWarranty') {
                this._initializeWarrantySheet(sheetEvent.instance);
            } else if (sheetEvent.instance.id === 'or_addToBasketBusinessError') {
                this._initializeMenuButtons();
            } else if (sheetEvent.instance.id === 'or_addToBasketTechnicalError') {
                this._initializeTechnicalErrorSheet();
            }
        });

        AddToBasketConfirmation.sheetV1?.afterCloseEvent.on((sheetEvent: { instance: HTMLElement }) => {
            if (sheetEvent.instance.id === 'or_addToBasketWarranty' && AddToBasketConfirmation.confirmationUrl) {
                AddToBasketConfirmation.openConfirmation(AddToBasketConfirmation.confirmationUrl);
            }
        });
    }

    private _initializeMenuButtons(): void {
        this._initializeClickableElement('.or_js_addToBasketConfirmation__continueShoppingButton', () => {
            this.trackButtonClick("StepWKBL_continue_shopping");
            this.closeActiveSheet();
        })

        this._initializeClickableElement('.or_js_addToBasketConfirmation__redirectToBasketButton', () => {
            this.trackButtonClick("StepWKBL_to_basket");
            window.o_order?.utils.goto(window, '/order/basket');
        })
    }

    private _initializeConfirmationSheet(): void {
        const content = document.querySelector<HTMLElement>('.or_addToBasketConfirmationSheet__content');
        const warrantyUrl = content?.dataset.warrantyUrl;
        if (warrantyUrl) {
            AddToBasketConfirmation.warrantyUrl = warrantyUrl;
        }

        this._initializeMenuButtons();
        this._initializeSpecialSizeRevertButtons();
        this._initializeExtraTagTracking();

        this._performanceMarketing.sendData();
        this.emitAdjustTracking();
        window.o_global.eventQBus.emit('ft1.order-core.addToBasketConfirmationOpened');
    }

    private _initializeExtraTagTracking() {
        this._initializeClickableElement('.or_js_priceReduction', () => {
            this._actionTracking.submitEvent(
                {name: 'click', features: []},
                {'order_InfoButton': ['discount']}
            );
        });
    }

    _initializeSpecialSizeRevertButtons() {
        this._initializeClickableElement('.or_js_addToBasketConfirmationSheet__specialSizeRevert', async (specialSizeRevertButton) => {
            await this.withMultiClickProtection(specialSizeRevertButton, async () => {
                const addToBasketElement = document.querySelector<HTMLElement>('.or_js_addToBasketConfirmationSheet__content');
                const addToBasketId = addToBasketElement?.dataset.addToBasketId;

                if (addToBasketId) {
                    await this.revertAddToBasketAndCloseSheet(addToBasketId);
                }
            });
        });

        this._initializeClickableElement('.or_js_addToBasketConfirmationSheet__specialSizeRevertSecondary', async (secondaryRevertButton) => {
            await this.withMultiClickProtection(secondaryRevertButton, async () => {
                const addToBasketElement = document.querySelector<HTMLElement>('.or_js_addToBasketConfirmationSheet__content');
                const addToBasketId = addToBasketElement?.dataset.addToBasketId;
                const basketItemId = secondaryRevertButton.dataset.basketItemId;

                if (addToBasketId && basketItemId) {
                    await this.removeAddedItemAndReloadSheet(addToBasketId, basketItemId);
                }
            });
        });
    }

    async withMultiClickProtection(element: HTMLElement, listener: () => Promise<void>): Promise<void> {
        if (element.dataset.inProgress === 'true') {
            return;
        }

        try {
            element.dataset.inProgress = 'true';
            await listener();
        } finally {
            delete element.dataset.inProgress;
        }
    }

    trackButtonClick(target: string) {
        this._actionTracking.submitEvent(
            {name: 'click', features: []},
            {"order_Flow": [target]}
        );
    }

    trackNotSelectLtgWarranty(button: HTMLElement) {
        const variationId = button?.dataset.variationId ?? "";
        const servicePrice = button?.dataset.servicePrice ?? "";
        const serviceName = button?.dataset.serviceName ?? "";
        const serviceId = button?.dataset.serviceId ?? "";
        const serviceCharge = servicePrice === '0.00' ? 'free' : 'charge'
        const serviceStatus = 'deselected'
        const serviceQuantity = '0' /*🤫*/

        const action: Action = {
            name: 'cancel',
            features: [
                {
                    id: 'order_service_sel',
                    name: 'ServiceCancelled',
                    status: 'cancelled',
                    labels: {}
                },
                {
                    id: 'order_service_sel_1',
                    variationId: variationId,
                    name: 'Variation',
                    parentId: 'order_service_sel',
                    status: 'visible',
                    labels: {}
                },
                {
                    id: 'order_service_sel_2',
                    name: 'Service',
                    parentId: 'order_service_sel_1',
                    status: 'cancelled',
                    labels: {
                        'order_Service': [serviceName],
                        'order_ServiceCharge': [serviceCharge],
                        'order_ServiceId': [serviceId],
                        'order_ServicePrice': [servicePrice],
                        'order_ServiceQuantity': [serviceQuantity],
                        'order_ServiceStatus': [serviceStatus],
                    }
                }
            ]
        };
        this._actionTracking.submitEventMerge(action);
    }

    private _initializeWarrantySheet(sheetElement: HTMLElement) {
        const content = sheetElement.querySelector<HTMLElement>('.or_addToBasketWarrantySheet__content');
        const confirmationUrl = content?.dataset.confirmationUrl;
        if (confirmationUrl) {
            AddToBasketConfirmation.confirmationUrl = confirmationUrl;
        }
        const warrantyUrl = content?.dataset.warrantyUrl;
        if (warrantyUrl) {
            AddToBasketConfirmation.warrantyUrl = warrantyUrl;
        }

        sheetElement.querySelector('.or_js_addToBasketWarranty__addWarrantyButton')
            ?.addEventListener('click', () => this.addWarrantyButtonAction());
        sheetElement.querySelector('.or_js_addToBasketWarranty__withoutWarrantyButton')
            ?.addEventListener('click', (event) => this.withoutWarrantyButtonAction(event.target as HTMLElement));

        window.o_global.eventQBus.emit('ft1.order-core.addToBasketWarrantyOpened');
    }

    private _initializeTechnicalErrorSheet() {
        this._initializeClickableElement('.or_js_addToBasketTechnicalError__backButton', () => {
            this.closeActiveSheet();
        });

        this._initializeClickableElement('.or_js_addToBasketTechnicalError__retryButton', () => {
            this.retryAddToBasket();
        });
    }

    private _initializeClickableElement(selector: string, action: (htmlElement: HTMLElement) => void) {
        const elements = [...document.querySelectorAll<HTMLElement>(selector)];

        elements
            .filter(element => element.dataset.initialized !== 'true')
            .forEach(element => {
                element.addEventListener('click', () => {
                    action(element);
                })
                element.dataset.initialized = 'true';
            });
    }

    private async addWarrantyButtonAction(): Promise<void> {
        if (this._addingWarranty) {
            return;
        }
        this._addingWarranty = true;

        try {
            if (AddToBasketConfirmation.warrantyUrl && AddToBasketConfirmation.confirmationUrl) {
                const response = await this.addWarranty(AddToBasketConfirmation.warrantyUrl);
                if (response.ok) {
                    this.closeActiveSheet();
                } else {
                    this.handleWarrantyError(await response.text());
                }
            }
        } finally {
            this._addingWarranty = false;
        }
    }

    private handleWarrantyError(errorHtml: string): void {
        const errorContainer = document.querySelector('.or_js_addToBasketConfirmationWarrantySheet__error');
        if (errorContainer) {
            errorContainer.innerHTML = errorHtml;
        }
    }

    private withoutWarrantyButtonAction(button: HTMLElement): void {
        if (AddToBasketConfirmation.confirmationUrl) {
            this.trackNotSelectLtgWarranty(button)
            this.closeActiveSheet();
        }
    }

    private addWarranty(url: string): Promise<Response> {
        return fetch(url, {method: 'POST'});
    }

    private async revertAddToBasketAndCloseSheet(addToBasketId: string): Promise<void> {
        const response = await fetch(`/order/addToBasket/confirmation/${addToBasketId}`, {
            method: 'DELETE',
        });

        if (response.ok) {
            this.trackDeleteItem();
            window.o_order.common.basketChange.triggerBasketChange();
            this.closeActiveSheet();
        }
    }

    private async removeAddedItemAndReloadSheet(addToBasketId: string, basketItemId: string): Promise<void> {
        const response = await fetch(`/order/addToBasket/confirmation/${addToBasketId}/${basketItemId}`, {method: 'DELETE'});

        if (response.ok) {
            this.trackDeleteItem();
            window.o_order.common.basketChange.triggerBasketChange();
            const responseBody = await response.json();
            AddToBasketConfirmation.sheetV1?.close();
            /*🤫*/
            const newSheetUrl = `${responseBody.sheetUrl}${responseBody.sheetUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
            AddToBasketConfirmation.openConfirmation(newSheetUrl);
        }
    }

    trackDeleteItem() {
        this._actionTracking.submitEvent(
            {name: 'click', features: []},
            {"order_AddToBasketCancel": ["specialsize"]}
        );
    }

    private emitAdjustTracking() {
        window.o_global.eventLoader.onAllPreloadScriptsLoaded(10, function () {
            const productElements = document.querySelectorAll<HTMLElement>('.or_js_addToBasketConfirmationSheet__product');
            const articleNumbers = [...productElements]
                .map(element => element.dataset.articleNumber)
                .filter(articleNumber => articleNumber !== undefined) as string[];

            if (articleNumbers.length > 0) {
                window.o_global.eventQBus.emit('ft-apps.adjust.eventTracking', {
                    eventName: {
                        android: 'hg253c',
                        ios: 'a5smk7'
                    },
                    partnerParameters: {
                        articleids: JSON.stringify(articleNumbers),
                        contentType: 'product'
                    }
                });

                window.o_order.utils.activateFeatureCompleted('adjust');
            }
        });
    }

    private closeActiveSheet() {
        AddToBasketConfirmation.sheetV1?.close();
    }

    private retryAddToBasket() {
        const dataAsString = sessionStorage.getItem("retryData");
        if (dataAsString) {
            AddToBasketConfirmation.sheetV1?.close();
            const dataAsJson = JSON.parse(dataAsString);
            window.o_order.api?.addToBasket(dataAsJson);
        }
    }

    static openWarranty(sheetUrl: string): void {
        AddToBasketConfirmation.sheetV1.create({
            id: 'or_addToBasketWarranty',
            url: sheetUrl,
            hideCloseButton: true,
            open: true
        });
    }

    static openConfirmation(sheetUrl: string): void {
        AddToBasketConfirmation.sheetV1.create({
            id: 'or_addToBasketConfirmation',
            url: sheetUrl,
            noContentPadding: true,
            open: true
        });
    }

    static openBusinessError(sheetUrl: string): void {
        AddToBasketConfirmation.sheetV1.create({
            id: 'or_addToBasketBusinessError',
            url: sheetUrl,
            noContentPadding: true,
            open: true
        });
    }

    static openTechnicalError(sheetUrl: string, retryData: string): void {
        sessionStorage.setItem("retryData", retryData);

        AddToBasketConfirmation.sheetV1.create({
            id: 'or_addToBasketTechnicalError',
            url: sheetUrl,
            noContentPadding: true,
            open: true
        });
    }
}
